import Box from '@material-ui/core/Box';
import CheckIcon from '@material-ui/icons/Check';
import { makeStyles } from '@material-ui/core/styles';

import IProduct, { ProductDocumentCategoryEnum } from 'models/product';

// Anchor id of the Produktdokument section; requirement callouts scroll here.
export const PRODUCT_DOCUMENTS_SECTION_ID = 'product-documents-section';

// A product condition that makes a specific supporting document relevant
// (e.g. PPWR answered "Ja" → a Declaration of Conformity). One entry drives both
// the inline confirmation and the auto-default category on upload. Add an entry
// to DOC_REQUIREMENTS to introduce a new conditional document — no new JSX needed.
// FC-1950: the document is not mandatory, so there is no prompt asking for it.
export interface DocRequirement {
  // The document category that satisfies this requirement. Stored doc.category
  // holds the backend code (e.g. 'PPWR_DOC'); the FE enum is Swedish-label-valued,
  // so config entries cast the code string to the enum type (see PPWR below).
  docCategory: ProductDocumentCategoryEnum;
  // When the document becomes relevant for the product.
  isTriggered: (state?: IProduct) => boolean;
  // Shown once the document is attached.
  confirm: string;
}

const hasDoc = (state: IProduct | undefined, category: ProductDocumentCategoryEnum) =>
  (state?.documents ?? []).some(doc => doc.category === category);

// PPWR (EU 2025/40): answering "Ja" makes the EU Declaration of Conformity relevant.
export const PPWR_DOC_REQUIREMENT: DocRequirement = {
  // doc.category stores the backend code; the FE enum is Swedish-label-valued.
  docCategory: 'PPWR_DOC' as ProductDocumentCategoryEnum,
  isTriggered: state => state?.compliancePpwrPackaging === true,
  confirm: 'EU-försäkran om överensstämmelse (PPWR) bifogad under Produktdokument.',
};

// All conditional document requirements. Add new ones here (e.g. eco-cert).
export const DOC_REQUIREMENTS: DocRequirement[] = [PPWR_DOC_REQUIREMENT];

// First requirement that's triggered but still missing its document — used to
// default the first uploaded file's category in the Produktdokument dropzone.
export const getUnsatisfiedDocCategory = (state?: IProduct): ProductDocumentCategoryEnum | undefined =>
  DOC_REQUIREMENTS.find(r => r.isTriggered(state) && !hasDoc(state, r.docCategory))?.docCategory;

const useStyles = makeStyles(() => ({
  confirm: {
    // Match the Magic Upload success text (FillProductByImage): CSS green + bold.
    color: 'green',
    fontWeight: 700,
  },
}));

interface DocRequirementCalloutProps {
  requirement: DocRequirement;
  state: IProduct;
}

// Per-requirement status shown under its triggering field: a green confirmation
// once the document is attached, nothing otherwise.
export const DocRequirementCallout = ({ requirement, state }: DocRequirementCalloutProps) => {
  const classes = useStyles();

  if (!requirement.isTriggered(state) || !hasDoc(state, requirement.docCategory)) return null;

  return (
    <Box display="flex" alignItems="center" className={classes.confirm} style={{ gap: 8, marginTop: 4 }}>
      <CheckIcon fontSize="small" />
      <span style={{ fontSize: 14 }}>{requirement.confirm}</span>
    </Box>
  );
};
